import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer } from 'lucide-react';

// ── Driver Earnings & Route Plan — a driver-FACING document ────────────────────
// Generated from the Driver Review page for the selected driver. It turns the
// per-parcel price + the driver's own pace into a plain-English plan showing two
// upsides of beating the scheduled parcels-per-hour: (1) a self-funded pay rise,
// (2) freed-up time to cover others' leave. All parcels-per-hour steps are 0.5.
//
// Rendered as a print-ready overlay (fixed light "document" theme so it looks the
// same and prints cleanly regardless of the app's dark/light mode).

// Fixed document palette (independent of app theme).
const INK = '#1a1a2e', GOLD = '#c9a84c', GREEN = '#1a9e5f', RED = '#dc2626',
      DIM = '#6b7280', LINE = '#e6e8ec', SOFT = '#f6f7f9', PAPER = '#ffffff';

const aud = (n, d = 2) => (isFinite(n) ? n : 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: d, maximumFractionDigits: d });
const n1 = (n) => (isFinite(n) ? n : 0).toLocaleString('en-AU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const n0 = (n) => Math.round(isFinite(n) ? n : 0).toLocaleString('en-AU');
// decimal hours → "Xh Ym"
const dur = (h) => {
  if (!isFinite(h) || h <= 0) return '0m';
  const m = Math.round(h * 60), hh = Math.floor(m / 60), mm = m % 60;
  return hh && mm ? `${hh}h ${mm}m` : hh ? `${hh}h` : `${mm}m`;
};

export default function DriverEarningsReport({ data, onClose }) {
  const {
    name, login, route, type, vehicle, vehicleClassLabel,
    periodStart, periodEnd, productiveDays,
    perParcel, perParcelGst, awardHourly, ratesEffective,
    medianPPH, bestPPH, avgParcelsPerDay, hoursYr,
  } = data;

  // Break-even pace: where per-parcel earnings equal the guaranteed award minimum.
  // At/above it he earns above the minimum; below it the IR guarantee tops him up.
  const bk = perParcel > 0 ? awardHourly / perParcel : 0;
  const eff = (p) => Math.max(p * perParcel, awardHourly);          // $/hr he actually receives
  const effMed = eff(medianPPH);

  // 0.5-increment pace domain centred on the story (guarantee → upside), ≤15 rows.
  const steps = useMemo(() => {
    const anchorLo = Math.min(bk, medianPPH || bk);
    const anchorHi = Math.max(bestPPH || bk, bk, medianPPH || bk);
    let lo = Math.max(0.5, Math.floor((anchorLo - 1.5) * 2) / 2);
    let hi = Math.ceil((anchorHi + 1.5) * 2) / 2;
    if (hi - lo > 7) hi = lo + 7;                                   // keep it readable
    const out = [];
    for (let p = lo; p <= hi + 1e-9; p += 0.5) out.push(+p.toFixed(1));
    return out;
  }, [bk, medianPPH, bestPPH]);
  const lo = steps[0], hi = steps[steps.length - 1];

  // ── Chart 1: $/hr vs pace (the hockey stick) ────────────────────────────────
  const C1 = { w: 660, h: 300, l: 62, r: 24, t: 22, b: 44 };
  const yMax1 = Math.ceil(eff(hi) / 5) * 5;
  const x1 = (p) => C1.l + ((p - lo) / (hi - lo)) * (C1.w - C1.l - C1.r);
  const y1 = (v) => C1.h - C1.b - (v / yMax1) * (C1.h - C1.t - C1.b);
  // piecewise-linear vertices: flat at award to bk, then rising to hi
  const linePts = [[lo, awardHourly], [Math.max(lo, Math.min(hi, bk)), awardHourly], [hi, eff(hi)]]
    .filter((pt, i, a) => i === 0 || pt[0] !== a[i - 1][0]);
  const linePath = linePts.map((pt, i) => `${i ? 'L' : 'M'}${x1(pt[0]).toFixed(1)},${y1(pt[1]).toFixed(1)}`).join(' ');
  // shaded "pay-rise zone" (area between the line and the award floor, above bk)
  const zone = bk < hi
    ? `M${x1(bk).toFixed(1)},${y1(awardHourly).toFixed(1)} L${x1(hi).toFixed(1)},${y1(eff(hi)).toFixed(1)} L${x1(hi).toFixed(1)},${y1(awardHourly).toFixed(1)} Z`
    : '';

  // ── Chart 2: hours to clear the day vs pace ─────────────────────────────────
  const C2 = { w: 660, h: 260, l: 62, r: 24, t: 22, b: 44 };
  const hoursAt = (p) => (p > 0 ? avgParcelsPerDay / p : 0);
  const yMax2 = Math.ceil(hoursAt(lo));
  const x2 = (p) => C2.l + ((p - lo) / (hi - lo)) * (C2.w - C2.l - C2.r);
  const y2 = (v) => C2.h - C2.b - (v / yMax2) * (C2.h - C2.t - C2.b);
  const curvePath = steps.map((p, i) => `${i ? 'L' : 'M'}${x2(p).toFixed(1)},${y2(hoursAt(p)).toFixed(1)}`).join(' ');
  // "you now" → "good days" freed-time band
  const fastPace = Math.max(medianPPH + 0.5, bestPPH || medianPPH);
  const fastClamped = Math.min(hi, fastPace);
  const freedDay = Math.max(0, hoursAt(medianPPH) - hoursAt(fastClamped));

  const sec = { marginBottom: 22 };
  const h2 = { fontSize: 15, fontWeight: 800, color: INK, margin: '0 0 4px', letterSpacing: '-0.01em' };
  const subh = { fontSize: 12, color: DIM, margin: '0 0 12px', lineHeight: 1.5 };
  const cardBox = { border: `1px solid ${LINE}`, borderRadius: 10, padding: '16px 18px', background: PAPER };

  const StatBox = ({ label, value, tone, note }) => (
    <div style={{ flex: '1 1 150px', minWidth: 140, background: SOFT, border: `1px solid ${LINE}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 10.5, color: DIM, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: tone || INK, marginTop: 3, lineHeight: 1.1 }}>{value}</div>
      {note && <div style={{ fontSize: 10.5, color: DIM, marginTop: 3 }}>{note}</div>}
    </div>
  );

  return createPortal(
    <div className="hp-report-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,25,0.72)', zIndex: 4000, overflowY: 'auto', padding: '24px 16px' }}>
      {/* Toolbar (screen only) */}
      <div className="no-print" style={{ maxWidth: 820, margin: '0 auto 12px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={() => window.print()} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: GOLD, color: INK, border: 'none', borderRadius: 8, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
          <Printer size={15} /> Print / Save PDF
        </button>
        <button onClick={onClose} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          <X size={15} /> Close
        </button>
      </div>

      {/* Document */}
      <div id="hp-report" style={{ maxWidth: 820, margin: '0 auto', background: PAPER, color: INK, borderRadius: 12, overflow: 'hidden', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
        {/* Masthead */}
        <div style={{ background: INK, color: '#fff', padding: '26px 34px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: GOLD, fontWeight: 800, fontSize: 18, letterSpacing: '0.4px' }}>HOLLYPAUL RECRUITMENT</div>
              <div style={{ color: '#cfd3dc', fontSize: 11, marginTop: 2 }}>Transport &amp; Logistics · Your Route &amp; Earnings Plan</div>
            </div>
            <div style={{ color: GOLD, fontSize: 10.5, fontWeight: 700, letterSpacing: '2px', textAlign: 'right' }}>PRIVATE &amp; CONFIDENTIAL</div>
          </div>
        </div>

        <div style={{ padding: '26px 34px 34px' }}>
          {/* Title + who */}
          <div style={{ ...sec }}>
            <h1 style={{ fontSize: 25, color: INK, margin: '0 0 6px', letterSpacing: '-0.02em' }}>Prepared for {name || login}</h1>
            <div style={{ fontSize: 12.5, color: DIM }}>
              Route <b style={{ color: INK }}>{route || '—'}</b>{type ? ` · ${type}` : ''} · Vehicle <b style={{ color: INK }}>{vehicle || vehicleClassLabel || '—'}</b>
              <br />Based on your own delivery record — {productiveDays} working days, {periodStart} to {periodEnd}.
            </div>
          </div>

          {/* Intro */}
          <div style={{ ...cardBox, ...sec, background: SOFT }}>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.65, color: '#2b303a' }}>
              You are paid a fixed price for every parcel you deliver. This plan shows exactly what that price is, and
              the single most important thing about it: <b style={{ color: INK }}>the faster and more efficiently you deliver, the more you earn per hour</b> —
              a pay rise you hand yourself, without ever having to ask. And if you would rather not take on more parcels,
              that same speed buys back your time — time you can use to help out and earn elsewhere. Your earnings can
              never fall below the guaranteed minimum, so the only direction from here is up.
            </p>
          </div>

          {/* Your price */}
          <div style={sec}>
            <h2 style={h2}>Your per-parcel price</h2>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch' }}>
              <div style={{ flex: '1 1 220px', background: INK, color: '#fff', borderRadius: 10, padding: '16px 20px' }}>
                <div style={{ fontSize: 10.5, color: GOLD, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Price per parcel</div>
                <div style={{ fontSize: 34, fontWeight: 900, marginTop: 2, lineHeight: 1 }}>{aud(perParcel, 3)}</div>
                <div style={{ fontSize: 11, color: '#cfd3dc', marginTop: 4 }}>{aud(perParcelGst, 3)} incl. GST</div>
              </div>
              <StatBox label="Scheduled pace" value={`${n1(bk)} /hr`} note="deliver this to hit the minimum" />
              <StatBox label="Guaranteed minimum" value={`${aud(awardHourly, 2)}/hr`} note="you can never earn less" />
              <StatBox label="Your pace now" value={`${n1(medianPPH)} /hr`} tone={medianPPH >= bk ? GREEN : INK} note={medianPPH >= bk ? 'already above schedule' : 'room to grow'} />
            </div>
            <p style={{ fontSize: 12, color: DIM, margin: '10px 2px 0', lineHeight: 1.55 }}>
              At the scheduled pace of <b style={{ color: INK }}>{n1(bk)} parcels per hour</b>, this price works out to exactly your
              guaranteed minimum of <b style={{ color: INK }}>{aud(awardHourly, 2)} per hour</b>. Every parcel you deliver <i>above</i> that
              pace is paid at the same rate — so it lands straight in your pocket.
            </p>
          </div>

          {/* The key idea */}
          <div style={{ ...cardBox, ...sec, borderLeft: `4px solid ${GOLD}` }}>
            <div style={{ fontWeight: 800, color: INK, marginBottom: 4, fontSize: 13.5 }}>The one idea that matters</div>
            <div style={{ fontSize: 12.5, lineHeight: 1.6, color: '#2b303a' }}>
              Beat <b>{n1(bk)} parcels per hour</b> and you can do one of two things with the gain — <b style={{ color: GREEN }}>earn more money</b> by
              delivering more parcels in the same hours, <b>or</b> <b style={{ color: GREEN }}>finish earlier</b> and free up your time. Your choice, every day.
            </div>
          </div>

          {/* Chart 1 — pay rise */}
          <div style={sec}>
            <h2 style={h2}>1 · Give yourself a pay rise</h2>
            <div style={subh}>Your pay per hour rises with every extra parcel-per-hour above the scheduled pace. Below it, the guarantee holds you steady — you never lose, but you only gain by going faster.</div>
            <div style={{ ...cardBox, overflowX: 'auto' }}>
              <svg viewBox={`0 0 ${C1.w} ${C1.h}`} width="100%" style={{ display: 'block', minWidth: 520 }}>
                {/* y gridlines + labels */}
                {Array.from({ length: 6 }, (_, i) => Math.round((yMax1 / 5) * i)).map(v => (
                  <g key={v}>
                    <line x1={C1.l} y1={y1(v)} x2={C1.w - C1.r} y2={y1(v)} stroke={LINE} />
                    <text x={C1.l - 8} y={y1(v) + 3.5} textAnchor="end" fontSize="10" fill={DIM}>${v}</text>
                  </g>
                ))}
                {/* pay-rise zone */}
                {zone && <path d={zone} fill={GOLD} opacity="0.18" />}
                {/* award floor line */}
                <line x1={C1.l} y1={y1(awardHourly)} x2={C1.w - C1.r} y2={y1(awardHourly)} stroke={DIM} strokeDasharray="5 4" />
                <text x={C1.w - C1.r} y={y1(awardHourly) - 6} textAnchor="end" fontSize="10.5" fontWeight="700" fill={DIM}>Guaranteed minimum {aud(awardHourly, 2)}/hr</text>
                {/* earnings line */}
                <path d={linePath} fill="none" stroke={GOLD} strokeWidth="3" strokeLinejoin="round" />
                {/* scheduled-pace marker */}
                <line x1={x1(bk)} y1={C1.t} x2={x1(bk)} y2={C1.h - C1.b} stroke={INK} strokeDasharray="3 3" opacity="0.55" />
                <text x={x1(bk)} y={C1.t - 6} textAnchor="middle" fontSize="10" fontWeight="700" fill={INK}>scheduled {n1(bk)}</text>
                {/* you-now marker */}
                {Math.abs(medianPPH - bk) > 0.05 && medianPPH >= lo && medianPPH <= hi && (
                  <>
                    <circle cx={x1(medianPPH)} cy={y1(effMed)} r="4.5" fill={GREEN} />
                    <text x={x1(medianPPH)} y={y1(effMed) - 9} textAnchor="middle" fontSize="10" fontWeight="700" fill={GREEN}>you now · {aud(effMed, 2)}/hr</text>
                  </>
                )}
                {/* x axis labels (every 0.5) */}
                {steps.map(p => (
                  <text key={p} x={x1(p)} y={C1.h - C1.b + 16} textAnchor="middle" fontSize="9.5" fill={DIM}>{n1(p)}</text>
                ))}
                <text x={(C1.l + C1.w - C1.r) / 2} y={C1.h - 4} textAnchor="middle" fontSize="10.5" fontWeight="700" fill={INK}>Parcels per hour</text>
              </svg>
            </div>
          </div>

          {/* Chart 2 — free up time */}
          <div style={sec}>
            <h2 style={h2}>2 · Or free up your time</h2>
            <div style={subh}>Same day’s parcels, delivered faster, means you finish sooner. On a typical day of {n0(avgParcelsPerDay)} parcels, lifting from {n1(medianPPH)} to {n1(fastClamped)} P/H frees about <b style={{ color: GREEN }}>{dur(freedDay)}</b> every day.</div>
            <div style={{ ...cardBox, overflowX: 'auto' }}>
              <svg viewBox={`0 0 ${C2.w} ${C2.h}`} width="100%" style={{ display: 'block', minWidth: 520 }}>
                {Array.from({ length: yMax2 + 1 }, (_, i) => i).map(v => (
                  <g key={v}>
                    <line x1={C2.l} y1={y2(v)} x2={C2.w - C2.r} y2={y2(v)} stroke={LINE} />
                    <text x={C2.l - 8} y={y2(v) + 3.5} textAnchor="end" fontSize="10" fill={DIM}>{v}h</text>
                  </g>
                ))}
                {/* freed-time band between now and faster */}
                {freedDay > 0 && (
                  <g>
                    <line x1={x2(medianPPH)} y1={y2(hoursAt(medianPPH))} x2={x2(medianPPH)} y2={y2(hoursAt(fastClamped))} stroke={GREEN} strokeWidth="2" />
                    <rect x={x2(medianPPH) - 2} y={y2(hoursAt(fastClamped))} width={Math.max(2, x2(fastClamped) - x2(medianPPH))} height={y2(hoursAt(medianPPH)) - y2(hoursAt(fastClamped))} fill={GREEN} opacity="0.14" />
                    <text x={(x2(medianPPH) + x2(fastClamped)) / 2} y={y2(hoursAt(fastClamped)) - 6} textAnchor="middle" fontSize="10" fontWeight="700" fill={GREEN}>+{dur(freedDay)} free</text>
                  </g>
                )}
                <path d={curvePath} fill="none" stroke={INK} strokeWidth="3" strokeLinejoin="round" />
                {medianPPH >= lo && medianPPH <= hi && <circle cx={x2(medianPPH)} cy={y2(hoursAt(medianPPH))} r="4.5" fill={INK} />}
                {fastClamped >= lo && fastClamped <= hi && <circle cx={x2(fastClamped)} cy={y2(hoursAt(fastClamped))} r="4.5" fill={GREEN} />}
                {steps.map(p => (
                  <text key={p} x={x2(p)} y={C2.h - C2.b + 16} textAnchor="middle" fontSize="9.5" fill={DIM}>{n1(p)}</text>
                ))}
                <text x={(C2.l + C2.w - C2.r) / 2} y={C2.h - 4} textAnchor="middle" fontSize="10.5" fontWeight="700" fill={INK}>Parcels per hour</text>
                <text transform={`rotate(-90 14 ${(C2.t + C2.h - C2.b) / 2})`} x="14" y={(C2.t + C2.h - C2.b) / 2} textAnchor="middle" fontSize="10" fill={DIM}>Hours to finish</text>
              </svg>
            </div>
          </div>

          {/* 0.5-increment table */}
          <div style={sec}>
            <h2 style={h2}>Your numbers, pace by pace</h2>
            <div style={subh}>Every half-parcel-per-hour, and what it means both ways. You can take the extra as money <i>or</i> as time — not both at once.</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 560 }}>
                <thead>
                  <tr style={{ background: INK, color: '#fff' }}>
                    {['Parcels / hr', 'Your pay / hr', 'vs guarantee', 'Finish the day in', 'Time freed / day', 'Extra / year *'].map((hd, i) => (
                      <th key={hd} style={{ padding: '8px 10px', textAlign: i === 0 ? 'left' : 'right', fontWeight: 700, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{hd}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {steps.map(p => {
                    const e = eff(p), over = e - awardHourly;
                    const freed = Math.max(0, hoursAt(medianPPH) - hoursAt(p));
                    const extraYr = Math.max(0, (e - effMed)) * hoursYr;
                    const isSched = Math.abs(p - bk) < 0.25;
                    const isNow = Math.abs(p - medianPPH) < 0.25;
                    const bg = isSched ? 'rgba(201,168,76,0.16)' : isNow ? 'rgba(26,158,95,0.10)' : (p % 1 === 0 ? PAPER : SOFT);
                    return (
                      <tr key={p} style={{ background: bg, borderBottom: `1px solid ${LINE}` }}>
                        <td style={{ padding: '7px 10px', fontWeight: 800, color: INK }}>
                          {n1(p)}{isSched ? <span style={{ color: GOLD, fontWeight: 700 }}> · scheduled</span> : isNow ? <span style={{ color: GREEN, fontWeight: 700 }}> · you now</span> : ''}
                        </td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 800, color: over > 0.005 ? GREEN : INK }}>{aud(e, 2)}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', color: over > 0.005 ? GREEN : DIM, fontWeight: 700 }}>{over > 0.005 ? `+${aud(over, 2)}` : 'minimum'}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right' }}>{dur(hoursAt(p))}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', color: freed > 0 ? GREEN : DIM, fontWeight: freed > 0 ? 700 : 400 }}>{freed > 0 ? `+${dur(freed)}` : '—'}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', color: extraYr > 0 ? GREEN : DIM, fontWeight: extraYr > 0 ? 700 : 400 }}>{extraYr > 0 ? `+${aud(extraYr, 0)}` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 10.5, color: DIM, marginTop: 6 }}>* Extra per year if you use the faster pace to deliver more parcels in the same hours (~{n0(hoursYr)} h/yr). Choose money <i>or</i> time — the “time freed” column is the same speed used the other way.</div>
          </div>

          {/* Freed time is worth something */}
          <div style={{ ...cardBox, ...sec, background: SOFT }}>
            <h2 style={{ ...h2, marginBottom: 6 }}>What your freed-up time is worth</h2>
            <div style={{ fontSize: 12.5, lineHeight: 1.65, color: '#2b303a' }}>
              When you finish your own round early, you become the person we turn to when someone is on <b>holiday, off sick, or away unexpectedly</b>.
              Covering part of another round is <b style={{ color: GREEN }}>extra income on top of your own day</b> — paid at parcel rates, same as your own work —
              and it keeps the whole team moving. The drivers who earn the most in our network are almost always the ones who finish early and lend a hand.
            </div>
          </div>

          {/* The condition / caveat */}
          <div style={{ ...cardBox, ...sec, borderLeft: `4px solid ${RED}`, background: '#fdf2f2' }}>
            <div style={{ fontWeight: 800, color: '#b91c1c', marginBottom: 4, fontSize: 13.5 }}>The one condition</div>
            <div style={{ fontSize: 12.5, lineHeight: 1.6, color: '#2b303a' }}>
              This upside only comes to you if you <b>commit to safe, steady, efficient delivery</b>. The extra earnings and the freed-up
              time both depend on beating the scheduled pace. If you don’t, nothing bad happens to your pay — the
              <b> IR guarantee and the NSW Transport Industry (Courier &amp; Taxi Truck) Determination</b> still protect you at
              <b> {aud(awardHourly, 2)} per hour</b> — but you won’t be reaching your full earning potential, and the most you will earn
              is the guaranteed minimum. The difference between “just the minimum” and “a pay rise you gave yourself” is entirely in
              your hands, one parcel at a time.
            </div>
            <div style={{ fontSize: 12.5, lineHeight: 1.6, color: '#2b303a', marginTop: 10 }}>
              This matters most at <b>peak times like Christmas</b>. The parcels come thick and fast, and most drivers naturally deliver
              <b> more parcels per hour</b> then than at any other time of year — so peak is your biggest chance to earn well above the
              guarantee. Coast through it and, even at Christmas, you’ll still only take home the minimum. Commit to the pace and
              <b style={{ color: GREEN }}> this is when you shine most</b> — make it count.
            </div>
          </div>

          {/* Footer */}
          <div style={{ borderTop: `2px solid ${GOLD}`, paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 10, color: DIM, flexWrap: 'wrap', gap: 6 }}>
            <span>Hollypaul Recruitment Pty Ltd · Prepared for {name || login} · Private &amp; Confidential</span>
            <span>Guaranteed minimum: Courier &amp; Taxi Truck Determination, effective {ratesEffective}</span>
          </div>
        </div>
      </div>

      {/* Print: hide the app root, let the portaled document flow & paginate
          normally (position:absolute would clip to a single page in Chrome). */}
      <style>{`
        @media print {
          html, body { background: #fff !important; }
          #root { display: none !important; }
          .no-print { display: none !important; }
          .hp-report-overlay { position: static !important; background: #fff !important; overflow: visible !important; padding: 0 !important; }
          #hp-report { max-width: none !important; box-shadow: none !important; border-radius: 0 !important; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>
    </div>,
    document.body
  );
}
